import React from "react"
import { useMutation } from "@apollo/client/react"
import {
  SuggestionTargetProvider,
  PickOverlay,
  type SuggestionGenerator,
} from "@love-irish/suggestion-targets"
import {
  GenerateIpaPronunciationMutation,
  GeneratePronunciationMutation,
  GenerateSummaryScaffoldMutation,
} from "@love-irish/graphql-api"

type NameCtx = { name?: string }
type ResourceCtx = { resourceId?: string }

/**
 * Wires the app's SHARED per-kind generators to the "Help me write" pick tool and renders the overlay.
 * Mounted inside AppShell so the Grug FAB and the page's tagged fields share one registry (they're in one
 * React tree). Only kinds reused across fields live here; a field that owns its API defines `generate` inline
 * on its write capability (see DescriptionEditor's content field). New shared kinds plug in here.
 */
export default function AppSuggestionProvider({ children }: { children: React.ReactNode }) {
  const [genIpa] = useMutation(GenerateIpaPronunciationMutation)
  const [genPron] = useMutation(GeneratePronunciationMutation)
  const [genSummary] = useMutation(GenerateSummaryScaffoldMutation)

  const generators: Partial<Record<string, SuggestionGenerator>> = {
    ipa: async (_t, ctx) => {
      const r = await genIpa({ variables: { text: (ctx as NameCtx).name ?? "", language: "IRISH" } })
      const p = r.data?.generateIpaPronunciation
      if (!p?.success) throw new Error(p?.errors?.[0]?.message ?? "Couldn't generate the IPA")
      return p.ipaPronunciation ?? ""
    },
    pronunciation: async (_t, ctx) => {
      const r = await genPron({ variables: { text: (ctx as NameCtx).name ?? "", language: "IRISH" } })
      const p = r.data?.generatePronunciation
      if (!p?.success) throw new Error(p?.errors?.[0]?.message ?? "Couldn't generate a pronunciation")
      return p.pronunciation ?? ""
    },
    summary: async (_t, ctx) => {
      const r = await genSummary({ variables: { resourceId: (ctx as ResourceCtx).resourceId ?? "" } })
      const p = r.data?.generateSummaryScaffold
      if (!p?.success) throw new Error(p?.errors?.[0]?.message ?? "Couldn't draft a summary")
      return p.summary ?? ""
    },
  }

  return (
    <SuggestionTargetProvider generators={generators}>
      <PickOverlay />
      {children}
    </SuggestionTargetProvider>
  )
}
